import { useId, useState } from 'react';
import { useAppConfig, useMemberOptions } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { Field, Modal, MoneyInput } from './ui';
import { IconCheck } from './Icons';
import { PasswordInput } from './PasswordInput';
import { nowHHMM, STATUS_LABELS, todayISO } from '@/lib/format';
import type { LeadStatus } from '@/lib/types';

interface NewLeadModalProps {
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
  busy: boolean;
}

/** Cadastro de cliente. As vendas entram depois, como comprovantes vinculados a ele. */
export function NewLeadModal({ onClose, onSubmit, busy }: NewLeadModalProps) {
  const { user, isAdmin } = useAuth();
  const memberOptions = useMemberOptions();
  const { data: config } = useAppConfig();
  const credentialsEnabled = config?.credentialsEnabled !== false;
  const passwordId = useId();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [ownerId, setOwnerId] = useState(user?.id ?? '');
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState(nowHHMM());
  const [status, setStatus] = useState<LeadStatus>('ACTIVE');
  const [profit, setProfit] = useState(0);
  const [deposit, setDeposit] = useState(0);
  const [notes, setNotes] = useState('');
  const [accountLogin, setAccountLogin] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  function submit() {
    if (name.trim().length < 2) {
      setError('Informe o nome do cliente (mínimo 2 caracteres).');
      return;
    }
    if (!ownerId) {
      setError('Selecione o membro responsável.');
      return;
    }
    setError(null);

    void onSubmit({
      name: name.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      status,
      profit,
      deposit,
      notes: notes.trim() || undefined,
      accountLogin: accountLogin.trim() || undefined,
      accountPassword: accountPassword || undefined,
      date,
      time,
      ownerId,
    });
  }

  return (
    <Modal
      title="Novo cliente"
      width={680}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? <span className="spinner" /> : <IconCheck />} Cadastrar cliente
          </button>
        </>
      }
    >
      <div className="grid grid3">
        <Field label="Nome do cliente *">
          <input className="inp" value={name} onChange={(event) => setName(event.target.value)} placeholder="Nome completo" autoFocus />
        </Field>
        <Field label="E-mail">
          <input className="inp" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="email@exemplo.com" />
        </Field>
        <Field label="Telefone">
          <input className="inp" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="(00) 00000-0000" />
        </Field>
      </div>

      <div className="grid grid3">
        {isAdmin ? (
          <Field label="Responsável *">
            <select className="inp" value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
              <option value="">Selecione…</option>
              {memberOptions.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
            </select>
          </Field>
        ) : null}

        <Field label="Status">
          <select className="inp" value={status} onChange={(event) => setStatus(event.target.value as LeadStatus)}>
            {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>

        <Field label="Lucro obtido (R$)">
          <MoneyInput value={profit} onChange={setProfit} />
        </Field>
      </div>

      <div className="grid grid3">
        <Field label="Data do cadastro">
          <input className="inp" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>
        <Field label="Horário">
          <input className="inp" type="time" value={time} onChange={(event) => setTime(event.target.value)} />
        </Field>
        <Field label="Depositado na corretora (R$)">
          <MoneyInput value={deposit} onChange={setDeposit} />
        </Field>
      </div>

      <div className="grid grid2">
        <Field label="Login da conta na casa" hint="Opcional — o usuário ou e-mail do cliente lá.">
          <input
            className="inp"
            value={accountLogin}
            onChange={(event) => setAccountLogin(event.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </Field>
        <div className="field">
          <label className="field-label" htmlFor={passwordId}>Senha da conta na casa</label>
          <PasswordInput
            id={passwordId}
            value={accountPassword}
            onChange={setAccountPassword}
            autoComplete="new-password"
            disabled={!credentialsEnabled}
            aria-describedby={`${passwordId}-dica`}
          />
          <span id={`${passwordId}-dica`} className="field-hint">
            {credentialsEnabled
              ? 'Opcional. Guardada criptografada — só o responsável e os administradores veem.'
              : 'Indisponível: falta configurar a chave de criptografia no servidor.'}
          </span>
        </div>
      </div>

      <Field label="Observações">
        <textarea className="inp" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="opcional" />
      </Field>

      <div className="muted" style={{ fontSize: 12 }}>
        As vendas deste cliente entram sozinhas pela compra no checkout (Cakto, Kirvano). Depósito, alavancagem e gás: abra o cliente e use
        "Anexar comprovante".
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
    </Modal>
  );
}
